import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { PermissionService } from '../services/supabase/permission.service';

/**
 * Structural directive: renders the host element only when the JWT carries
 * the given permission code. Reactively re-evaluates when permissions change
 * (e.g., after a practice switch).
 *
 * Usage:
 *   <button *hasPermission="'discharge_questions.edit'">Edit</button>
 */
@Directive({
  // Structural directive used as `*hasPermission` — an `app`-prefixed selector
  // would read awkwardly at the call site, so we keep the bare name.
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private readonly tpl = inject(TemplateRef<unknown>);
  private readonly vcr = inject(ViewContainerRef);
  private readonly perms = inject(PermissionService);

  private readonly code = signal<string>('');
  private rendered = false;

  constructor() {
    effect(() => {
      const c = this.code();
      const allowed = !c || this.perms.has(c);
      if (allowed && !this.rendered) {
        this.vcr.createEmbeddedView(this.tpl);
        this.rendered = true;
      } else if (!allowed && this.rendered) {
        this.vcr.clear();
        this.rendered = false;
      }
    });
  }

  @Input({ required: true }) set hasPermission(value: string) {
    this.code.set(value);
  }
}
